import { FC, useContext, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { FormProvider } from 'react-hook-form';

import FiltersContext from '@/context/FiltersContext/FiltersContext';
import { FilterType } from '@/models';
import { Period } from '@/types/date';
import collectAllLocalFilters from '@/utils/filter/collectAllLocalFilters';
import Button from '@/storybook/components/Button/Button';
import Dialog from '@/storybook/components/Dialog/Dialog';
import FilterSetPreview from '@/storybook/components/FilterSetPreview/FilterSetPreview';
import Checkbox from '@/storybook/components/FormCollection/Checkbox/Checkbox';
import Input from '@/storybook/components/FormCollection/Input/Input';
import { type FilterChipItem } from '@/storybook/components/FilterSetPreview/FilterSetPreview';
import useFilterNameForm, { FILTER_SET_NAME_MAX_LENGTH } from '@/hooks/useFilterNameForm';

import {
  getActiveSectionKey,
  getActiveChannelKey,
  isValidPeriod,
  toDayjs,
  buildDateRangeText,
  buildGlobalChips,
  buildLocalSections,
} from './SaveFiltersDialog.utils';
import styles from './SaveFiltersDialog.module.css';

export interface SaveFiltersDialogProps {
  isOpen: boolean;
  isLoading: boolean;
  onSave: (filterName: string, isDefault: boolean) => void | Promise<void>;
  onCancel: () => void;
}

const SaveFiltersDialog: FC<SaveFiltersDialogProps> = ({ isOpen, isLoading, onSave, onCancel }) => {
  const { t } = useTranslation();
  const { filterState } = useContext(FiltersContext);
  const { pathname } = useLocation();

  const { isDefault, isSaveDisabled, formMethods, handleDefaultChange, handleSave, reset } = useFilterNameForm();

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const handleSubmit = useCallback(() => {
    handleSave(onSave);
  }, [handleSave, onSave]);

  const handleClose = useCallback(() => {
    reset();
    onCancel();
  }, [onCancel, reset]);

  const dateRangeText = useMemo(() => {
    const dateRangeValue = filterState[FilterType.DATE_RANGE];
    const comparisonPeriodValue = filterState[FilterType.COMPARISON_PERIOD];
    if (!isValidPeriod(dateRangeValue)) return undefined;
    const dateRange: Period = {
      beginn: toDayjs(dateRangeValue.beginn),
      end: toDayjs(dateRangeValue.end),
      period: dateRangeValue.period,
    };
    const comparison = isValidPeriod(comparisonPeriodValue)
      ? {
          beginn: toDayjs(comparisonPeriodValue.beginn),
          end: toDayjs(comparisonPeriodValue.end),
          period: comparisonPeriodValue.period,
        }
      : undefined;
    return buildDateRangeText(dateRange, comparison, t);
  }, [filterState, t]);

  const globalFilterChips: FilterChipItem[] = useMemo(() => buildGlobalChips(filterState, t), [filterState, t]);

  const localFilterSections = useMemo(() => {
    if (!isOpen) return [];
    return buildLocalSections(collectAllLocalFilters(), t);
  }, [isOpen, t]);

  const footer = (
    <>
      <Button variant="outlined" onClick={handleClose} disabled={isLoading} data-testid="save-filters-cancel">
        {t('global.filters.saveFiltersDialog.cancel')}
      </Button>
      <Button
        variant="primary"
        onClick={handleSubmit}
        disabled={isSaveDisabled || isLoading}
        loading={isLoading}
        data-testid="save-filters-save"
      >
        {t('global.filters.saveFiltersDialog.save')}
      </Button>
    </>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title={t('global.filters.saveFiltersDialog.title')}
      size="L"
      footer={footer}
      className={styles.saveFiltersDialog}
      data-testid="save-filters-dialog"
    >
      <div>
        <FormProvider {...formMethods}>
          <Input
            name="filterSetName"
            registerOptions={{ maxLength: FILTER_SET_NAME_MAX_LENGTH }}
            placeholder={String(t('global.filters.saveFiltersDialog.filterSetNamePlaceholder'))}
            maxLength={FILTER_SET_NAME_MAX_LENGTH}
            data-testid="save-filters-name-input"
          />
        </FormProvider>
      </div>

      <Checkbox
        checked={isDefault}
        onChange={handleDefaultChange}
        label={t('global.filters.saveFiltersDialog.setAsDefault')}
        className={styles.defaultCheckbox}
        data-testid="save-filters-default-checkbox"
      />

      <FilterSetPreview
        title={t('global.filters.saveFiltersDialog.filtersToBeSaved')}
        dateRangeLabel={t('global.filters.saveFiltersDialog.interactionDateRange')}
        dateRangeText={dateRangeText}
        globalFilterLabel={t('global.filters.saveFiltersDialog.filterHCPs')}
        globalFilterChips={globalFilterChips}
        localFilterSections={localFilterSections}
        activeSectionKey={getActiveSectionKey(pathname)}
        activeChannelKey={getActiveChannelKey(pathname)}
        inactiveLabel={String(t('global.filters.saveFiltersDialog.inactiveOnThisPage'))}
        data-testid="save-filters-preview"
      />
    </Dialog>
  );
};

export default SaveFiltersDialog;
